import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import './reviews-table.css';

/**
 * Props:
 *  - show: boolean
 *  - onClose: () => void
 *  - role: "employee" | "manager"
 *  - value: { id, name, weight, description?, ... } | null
 *  - review: { id, employee_rating_id, employee_description, employee_comment, manager_rating_id, manager_description, manager_comment } | null
 *  - onSave: ({ id, value }) => Promise<void>    // id = review.id, value = new comment
 *  - loadValueDetails?: (valueId: number) => Promise<object?>  // optional enrichment
 *  - contractId?: number
 */
export default function CompanyValueSummaryModal({
                                                     show,
                                                     onClose,
                                                     role = 'employee',
                                                     value,
                                                     review,
                                                     onSave,
                                                     loadValueDetails,
                                                     contractId, // currently unused, kept for parity with KPA modal
                                                 }) {
    const [busy, setBusy] = useState(false);
    const [details, setDetails] = useState(null);
    const [comment, setComment] = useState('');

    const canEdit = role === 'employee' || role === 'manager';

    // Prime local comment from incoming review
    useEffect(() => {
        if (!review) {
            setComment('');
            return;
        }
        setComment(role === 'employee' ? (review.employee_comment ?? '') : (review.manager_comment ?? ''));
    }, [review, role]);

    // Optionally enrich value details
    useEffect(() => {
        let alive = true;
        (async () => {
            if (!show) return;
            if (!value?.id || !loadValueDetails) {
                setDetails(null);
                return;
            }
            try {
                const full = await loadValueDetails(value.id);
                if (alive) setDetails(full || null);
            } catch {
                if (alive) setDetails(null);
            }
        })();
        return () => { alive = false; };
    }, [show, value?.id, loadValueDetails]);

    const v = useMemo(() => ({ ...(value || {}), ...(details || {}) }), [value, details]);

    const handleSave = async () => {
        if (!review?.id) return;
        setBusy(true);
        try {
            await onSave({ id: review.id, value: comment });
            onClose?.();
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (!show) return;
        document.body.classList.add('modal-open');
        return () => { document.body.classList.remove('modal-open'); };
    }, [show]);

    if (!show) return null;

    // Render modal + backdrop to the end of <body> to avoid stacking-context issues
    return ReactDOM.createPortal(
        <>
            {/* Backdrop */}
            <div
                className="modal-backdrop fade show"
                onClick={onClose}
                aria-hidden="true"
                style={{ cursor: 'pointer' }}
            />

            {/* Modal */}
            <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
                <div className="modal-dialog modal-lg modal-dialog-centered" role="document" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Company Value Details</h5>
                            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                        </div>

                        <div className="modal-body">
                            {/* Static details */}
                            <div className="mb-3">
                                <div className="fw-semibold">Name</div>
                                <div className="text-muted">{v?.name ?? '—'}</div>
                            </div>

                            <div className="row g-3">
                                <div className="col-md-4">
                                    <div className="fw-semibold">Weight</div>
                                    <div className="text-muted">{v?.weight != null ? Number(v.weight).toFixed(2) : '—'}</div>
                                </div>
                                <div className="col-md-8">
                                    <div className="fw-semibold">Description</div>
                                    <div className="text-muted">{v?.description ?? '—'}</div>
                                </div>
                            </div>

                            <hr className="my-3" />

                            {/* Ratings (read-only) */}
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="fw-semibold mb-1">Employee Rating</div>
                                    <div className="d-flex gap-2">
                                        <input className="form-control form-control-sm" value={review?.employee_rating_id ?? ''} disabled />
                                        <input className="form-control form-control-sm" value={review?.employee_description ?? ''} disabled />
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="fw-semibold mb-1">Manager Rating</div>
                                    <div className="d-flex gap-2">
                                        <input className="form-control form-control-sm" value={review?.manager_rating_id ?? ''} disabled />
                                        <input className="form-control form-control-sm" value={review?.manager_description ?? ''} disabled />
                                    </div>
                                </div>
                            </div>

                            <hr className="my-3" />

                            {/* Editable comment (only one side enabled) */}
                            <div className="mb-2 fw-semibold">
                                {role === 'employee' ? 'Employee Comment' : 'Manager Comment'}
                            </div>
                            <textarea
                                className="form-control form-control-sm"
                                rows={4}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                disabled={!canEdit || !review?.id}
                                placeholder={review?.id ? 'Add your comment…' : 'No review row yet'}
                            />
                            {(!review?.id) && (
                                <div className="form-text text-warning">
                                    No review row exists yet for this value. Save will create it (upsert) via your handler.
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-light btn-sm" onClick={onClose} disabled={busy}>Close</button>
                            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={busy || !review?.id}>
                                {busy ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}